import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { UserApp } from '../services/api';

interface EditAppDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  app: UserApp;
  onSubmit: (data: { name: string; description?: string }) => Promise<void>;
}

const EditAppDialog: React.FC<EditAppDialogProps> = ({
  open,
  onOpenChange,
  app,
  onSubmit,
}) => {
  const [formData, setFormData] = useState({
    name: '',
    description: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Initialize form data when app changes
  useEffect(() => {
    if (app) {
      setFormData({
        name: app.name,
        description: app.description || ''
      });
      setErrors({});
    }
  }, [app]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'App name is required';
    } else if (formData.name.length > 100) {
      newErrors.name = 'App name must not exceed 100 characters';
    }
    
    if (formData.description && formData.description.length > 500) {
      newErrors.description = 'Description must not exceed 500 characters';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        name: formData.name.trim(),
        ...(formData.description.trim() && { description: formData.description.trim() })
      };
      
      await onSubmit(submitData);
    } catch (error) {
      console.error('Error updating app:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const hasChanges = () => {
    return formData.name !== app.name || formData.description !== (app.description || '');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] max-h-[85vh] overflow-hidden p-0">
        <form onSubmit={handleSubmit} className="flex flex-col max-h-[85vh]">
          <div className="px-6 pt-6 pb-4 flex-shrink-0">
            <DialogHeader className="space-y-3">
              <DialogTitle className="text-xl font-semibold">Edit Application</DialogTitle>
              <DialogDescription>
                Update your application's name and description.
              </DialogDescription>
            </DialogHeader>
          </div>

          <div className="flex-1 overflow-y-auto px-6">
            <div className="space-y-5 pb-4">
              <div className="space-y-3">
                <Label htmlFor="edit-name" className="text-sm font-medium">
                  Application Name *
                </Label>
                <Input
                  id="edit-name"
                  type="text"
                  placeholder="My LiveKit App"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className="bg-background border-input focus:border-ring w-full"
                  maxLength={100}
                  required
                />
                {errors.name && (
                  <p className="text-destructive text-xs">{errors.name}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.name.length}/100 characters
                </p>
              </div>

              <div className="space-y-3">
                <Label htmlFor="edit-description" className="text-sm font-medium">
                  Description (Optional)
                </Label>
                <Textarea
                  id="edit-description"
                  placeholder="Brief description of your application..."
                  value={formData.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="bg-background border-input focus:border-ring resize-none w-full"
                  rows={4}
                  maxLength={500}
                />
                {errors.description && (
                  <p className="text-destructive text-xs">{errors.description}</p>
                )}
                <p className="text-xs text-muted-foreground">
                  {formData.description.length}/500 characters
                </p>
              </div>
            </div>
          </div>

          <div className="px-6 pb-6 pt-4 border-t flex-shrink-0">
            <DialogFooter className="gap-2">
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={loading || !formData.name.trim() || !hasChanges()}
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                    Updating...
                  </>
                ) : (
                  'Update Application'
                )}
              </Button>
            </DialogFooter>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default EditAppDialog;
